import React from 'react'
import { EyeOff, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import LikesCount from '@/components/LikesCount'
import CommentsCount from '@/components/CommentsCount'
import { useTranslation } from '@/hooks/useTranslation'
import type { MediaTileData } from './types'

interface Props {
  item: MediaTileData
  onClick: (item: MediaTileData) => void
  selectionMode: boolean
  selected: boolean
  onToggleSelect: (id: string) => void
  showComments: boolean
  showLikes?: boolean
  commentsCount?: number
  votesCount?: number
  isCommentedByCurrentUser?: boolean
  isVotedByCurrentUser?: boolean
  className?: string
  tileClassName?: string
}

export const MediaTile: React.FC<Props> = ({
  item,
  onClick,
  selectionMode,
  selected,
  onToggleSelect,
  showComments,
  showLikes = false,
  commentsCount,
  votesCount,
  isCommentedByCurrentUser,
  isVotedByCurrentUser,
  className,
  tileClassName,
}) => {
  const { t } = useTranslation()

  const handleClick = () => {
    if (selectionMode) {
      onToggleSelect(item.id)
    } else {
      onClick(item)
    }
  }

  const displayUrl = item.cdn_display_s || item.cdn_thumb || item.secure_url

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={selectionMode ? selected : undefined}
      aria-label={selectionMode ? t('selectPhotos') : t('viewLarge')}
      className={cn(
        'group relative aspect-square w-full overflow-hidden rounded-lg border bg-muted transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        selectionMode && selected ? 'scale-90 border-4 border-secondary' : '',
        'transform transition-transform duration-150 ease-out',
        className,
        tileClassName,
      )}
    >
      <img
        src={displayUrl}
        alt={t('mediaAlt')}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.02]"
      />

      {/* Hover/active overlay */}
      <div className="pointer-events-none absolute inset-0 bg-black/0 transition group-hover:bg-black/10" aria-hidden />

      {/* Flag badge */}
      {item.flagged && (
        <div className="pointer-events-none absolute right-2 top-2 rounded-full bg-red-500 p-2" aria-hidden>
          <EyeOff strokeWidth='1.2' className="h-8 w-8 text-white" />
        </div>
      )}

      {/* Selection checkmark (only in selection mode) */}
      {selectionMode && (
        <div className="absolute left-2 top-2 z-10">
          <span
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-sm border bg-white/90 shadow-sm',
              selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
            )}
            aria-hidden
          >
            {selected && <Check className="h-4 w-4" />}
          </span>
        </div>
      )}

      {/* Bottom overlays */}
      <div className="pointer-events-none absolute inset-x-2 bottom-2 flex items-end justify-start gap-3" aria-hidden>
        {showComments && (
          <CommentsCount
            count={commentsCount ?? item.comments_count ?? 0}
            isCommentedByCurrentUser={isCommentedByCurrentUser ?? item.current_user_commented ?? false}
          />
        )}
        {showLikes && (
          <LikesCount
            count={votesCount ?? item.votes_count ?? 0}
            isVotedByCurrentUser={isVotedByCurrentUser ?? item.current_user_voted ?? false}
          />
        )}
      </div>
    </button>
  )
}
